/**
 * Push an event onto the GTM/GA4 dataLayer, ensuring it exists first.
 * Centralises the `window.dataLayer = window.dataLayer || []` guard that was
 * repeated across the checkout/builder pages.
 */
export function pushDataLayer(event) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
}

/**
 * Push a GA4 ecommerce event (view_item_list, view_item, add_to_cart, begin_checkout, purchase).
 * Clears the previous `ecommerce` object first: GTM merges pushes into one data model, arrays index by
 * index, so without the reset a purchase's single item only overwrote items[0] of the search results'
 * view_item_list and GA4 received the booked hotel plus the rest of the result list.
 */
export function pushEcommerce(event) {
    pushDataLayer({ ecommerce: null });
    pushDataLayer(event);
    matomoEcommerce(event);
}

/*
 * Matomo (self-hosted website statistics, same site as www. - Olympic:MatomoSiteId, 1 in every Octopus
 * environment; the tracker is set up in _Layout.cshtml).
 * The booking engine is a single-page app, so page views are sent here on each route change rather than
 * on page load, and the GA4 ecommerce events above are mirrored to Matomo's ecommerce API.
 */
let matomoLastUrl = null;
let matomoProductViewTimer = null;

function paq(...command) {
    window._paq = window._paq || [];
    window._paq.push(command);
}

function matomoPageView() {
    const url = window.location.href;
    if (url === matomoLastUrl) {
        return;
    }
    if (matomoLastUrl) {
        paq("setReferrerUrl", matomoLastUrl);
    }
    paq("setCustomUrl", url);
    paq("setDocumentTitle", document.title);
    paq("trackPageView");
    matomoLastUrl = url;
}

/**
 * Call on every route change. Hotel (builder) pages wait for their view_item so the page view carries
 * Matomo's product view (setEcommerceView must come before trackPageView); if the builder never
 * finishes loading, a plain page view is sent after 10 seconds.
 */
export function matomoRouteChange(isProductPage) {
    clearTimeout(matomoProductViewTimer);
    if (isProductPage) {
        matomoProductViewTimer = setTimeout(matomoPageView, 10000);
    } else {
        // Let the new page set its title first.
        setTimeout(matomoPageView, 300);
    }
}

function matomoEcommerce(event) {
    const ecommerce = event.ecommerce || {};
    const items = ecommerce.items || [];
    const addItems = () => {
        paq("clearEcommerceCart");
        items.forEach((item) =>
            paq(
                "addEcommerceItem",
                String(item.item_id ?? ""),
                item.item_name || "",
                item.item_category || "",
                Number(item.price) || 0,
                Number(item.quantity) || 1
            )
        );
    };
    const itemsTotal = () =>
        items.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 1), 0);

    switch (event.event) {
        case "view_item": {
            // Only alongside a page view: the builder can re-push view_item for the same URL after repricing,
            // and a product view set without one would be attached to the next page instead.
            const item = items[0] || {};
            clearTimeout(matomoProductViewTimer);
            if (window.location.href !== matomoLastUrl) {
                paq("setEcommerceView", String(item.item_id ?? ""), item.item_name || "", item.item_category || "", Number(item.price) || 0);
                matomoPageView();
            }
            break;
        }
        case "add_to_cart":
        case "begin_checkout":
            addItems();
            paq("trackEcommerceCartUpdate", itemsTotal());
            break;
        case "purchase": {
            // Matomo ignores a repeated order id, so a refreshed thank-you page is not counted twice.
            const total = Number(ecommerce.value) || itemsTotal();
            addItems();
            paq(
                "trackEcommerceOrder",
                String(ecommerce.transaction_id ?? ""),
                total,
                total,
                Number(ecommerce.tax) || 0,
                Number(ecommerce.shipping) || 0,
                false
            );
            break;
        }
    }
}
